import { Intent } from "@blueprintjs/core";
import {
  ClientAnalyticsEventType,
  DataBrowserSemanticIdType,
  SpecialVersionType,
  createExploreCellPayload,
  hexIdToHumanHexId,
  semanticDatasetTitleString,
} from "@hex/common";
import React, { useCallback, useState } from "react";
import { useHistory } from "react-router";
import { useTheme } from "styled-components";

import { useCreateExploreFromSemanticResourceCallback } from "../../../explore-v2/useCreateExplore.js";
import {
  HexButton,
  HexMenu,
  HexMenuItem,
  HexPopover,
  HexTooltip,
} from "../../../hex-components/index.js";
import { ORG_ID } from "../../../orgs.js";
import { Routes } from "../../../route/routes.js";
import { trackEvent } from "../../../util/trackEvent.js";
import { useToaster } from "../../common/Toasts.js";
import { usePinData } from "../../data/schema-browser-v2/usePinData.js";
import { useFeatureGates } from "../../feature-gate/FeatureGateContext.js";
import { FeatureGateToolTip } from "../../feature-gate/FeatureGateToolTip.js";
import { useCreateHexFromHomeMutation } from "../../home/data-tab/useHomeTableActions.generated.js";
import {
  ExploreIcon,
  MoreMenuIcon,
  SemanticDatasetIcon,
  SemanticViewIcon,
  StarActiveIcon,
} from "../../icons/CustomIcons.js";
import { DEFAULT_PROJECT_TITLE } from "../../logic/shared.js";
import {
  PinnedSemanticDatasetFragment,
  PinnedSemanticViewFragment,
} from "../../sidebar/data-browser/FavoritesView.generated.js";

import { FavoriteDataMenuItem } from "./FavoriteDataTableRow.js";
import { FixedHeightMenuItem } from "./styled.js";
import { isModifiedEvent } from "./utils.js";

export const FavoriteDataSemanticResourceRow = React.memo(
  function FavoriteDataSemanticResourceRow({
    semanticResource,
  }: {
    semanticResource:
      | PinnedSemanticDatasetFragment
      | PinnedSemanticViewFragment;
  }) {
    const history = useHistory();

    const onClick = useCallback(
      (evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        const withModifier = isModifiedEvent(evt);
        const urlParams = {
          tab: "semantic-layer" as const,
          detailViewType:
            semanticResource.__typename === "SemanticDataset"
              ? DataBrowserSemanticIdType.DATASET
              : DataBrowserSemanticIdType.VIEW,
          dataSourceId: semanticResource.semanticProject.id,
          semanticResourceName: semanticResource.name,
        };
        if (withModifier) {
          const url = Routes.DATA_BROWSER.href(ORG_ID, true, urlParams);
          window.open(url, "_blank");
          return;
        }
        Routes.DATA_BROWSER.push(history, urlParams);
      },
      [
        history,
        semanticResource.__typename,
        semanticResource.name,
        semanticResource.semanticProject.id,
      ],
    );

    return (
      <HexTooltip
        content={semanticDatasetTitleString(semanticResource)}
        // eslint-disable-next-line react/jsx-no-bind -- renderTarget
        renderTarget={({ isOpen: _isOpen, ref, ...tooltipTarget }) => (
          <FixedHeightMenuItem
            ref={ref}
            {...tooltipTarget}
            icon={
              semanticResource.__typename === "SemanticDataset" ? (
                <SemanticDatasetIcon />
              ) : (
                <SemanticViewIcon />
              )
            }
            text={
              <FavoriteDataMenuItem
                $useMono={false}
                hoverContent={
                  <FavoriteDataSemanticResourceMenuButton
                    semanticResource={semanticResource}
                  />
                }
                id={`${semanticResource.id}-sidebar-hover-container`}
                text={semanticDatasetTitleString(semanticResource)}
              />
            }
            onClick={onClick}
          />
        )}
      />
    );
  },
);

const FavoriteDataSemanticResourceMenuButton = React.memo(
  function FavoriteDataSchemaMenuButton({
    semanticResource,
  }: {
    semanticResource:
      | PinnedSemanticDatasetFragment
      | PinnedSemanticViewFragment;
  }) {
    const [isOpen, setIsOpen] = useState(false);

    const onPopoverInteraction = useCallback(
      (newIsOpen: boolean, e?: React.SyntheticEvent) => {
        setIsOpen(newIsOpen);
        // prevent event from bubbling up to the parent and
        // triggering data bento row click
        e?.stopPropagation();
      },
      [setIsOpen],
    );

    return (
      <HexPopover
        captureDismiss={true}
        content={
          <FavoriteDataSemanticResourceRowMenu
            semanticResource={semanticResource}
          />
        }
        isOpen={isOpen}
        minimal={true}
        position="bottom-left"
        // eslint-disable-next-line react/jsx-no-bind -- renderTarget
        renderTarget={({ isOpen: _isOpen, ref, ...popoverTarget }) => (
          <HexButton
            ref={ref}
            icon={<MoreMenuIcon />}
            minimal={true}
            small={true}
            {...popoverTarget}
          />
        )}
        onInteraction={onPopoverInteraction}
      />
    );
  },
);

const FavoriteDataSemanticResourceRowMenu = React.memo(
  function FavoriteDataSemanticResourceRowMenu({
    semanticResource,
  }: {
    semanticResource:
      | PinnedSemanticDatasetFragment
      | PinnedSemanticViewFragment;
  }) {
    const createExplore = useCreateExploreFromSemanticResourceCallback();
    const createExploreAndTrack = useCallback(async () => {
      await createExplore(
        semanticResource.__typename === "SemanticDataset"
          ? { datasetId: semanticResource.id }
          : { viewId: semanticResource.id },
        {
          trackingInfo: {
            browserType: "HOME_SIDEBAR",
            semanticProjectId: semanticResource.semanticProject.id,
            dataBrowserTab: "FAVORITES",
          },
        },
      );
    }, [
      createExplore,
      semanticResource.id,
      semanticResource.semanticProject.id,
      semanticResource.__typename,
    ]);

    const { exploreFromScratch: exploreFromScratchFeatureGate } =
      useFeatureGates();

    const pinData = usePinData();
    const history = useHistory();
    const toaster = useToaster();

    const [createHex] = useCreateHexFromHomeMutation();

    const createExploreCell = useCallback(() => {
      if (!semanticResource.semanticProject.dataConnection) {
        return;
      }
      createHex({
        variables: {
          name: DEFAULT_PROJECT_TITLE,
          description: "",
          dataConnectionIds: [
            semanticResource.semanticProject.dataConnection.id,
          ],
          cellsToCreate: [
            createExploreCellPayload({
              input:
                semanticResource.__typename === "SemanticDataset"
                  ? {
                      semanticDatasetName: semanticResource.name,
                      semanticProjectId: semanticResource.semanticProject.id,
                      dataConnectionId:
                        semanticResource.semanticProject.dataConnection.id,
                    }
                  : {
                      semanticViewName: semanticResource.name,
                      semanticProjectId: semanticResource.semanticProject.id,
                      dataConnectionId:
                        semanticResource.semanticProject.dataConnection.id,
                    },
            }),
          ],
        },
      })
        .then((result) => {
          if (result.data?.createHex.id) {
            history.push(
              Routes.LOGIC.getUrl({
                hexLocator: hexIdToHumanHexId(
                  result.data?.createHex.id,
                  DEFAULT_PROJECT_TITLE,
                ),
                version: SpecialVersionType.DRAFT,
              }),
            );
            if (semanticResource.__typename === "SemanticDataset") {
              trackEvent(ClientAnalyticsEventType.SEMANTIC_DATASET_SELECTED, {
                browserType: "HOME_SIDEBAR",
                tableActionTaken: "EXPLORE_UI",
                semanticProjectId: semanticResource.semanticProject.id,
                semanticDatasetId: semanticResource.id,
                dataBrowserTab: "FAVORITES",
              });
            } else {
              trackEvent(ClientAnalyticsEventType.SEMANTIC_VIEW_SELECTED, {
                browserType: "HOME_SIDEBAR",
                tableActionTaken: "EXPLORE_UI",
                semanticProjectId: semanticResource.semanticProject.id,
                semanticViewId: semanticResource.id,
                dataBrowserTab: "FAVORITES",
              });
            }
          } else {
            toaster.show({
              message: "Failed to create project",
              intent: Intent.DANGER,
            });
          }
        })
        .catch((e) => {
          console.error(e);
          toaster.show({
            message: "Failed to create project",
            intent: Intent.DANGER,
          });
        });
    }, [
      createHex,
      history,
      semanticResource.id,
      semanticResource.__typename,
      semanticResource.name,
      semanticResource.semanticProject.dataConnection,
      semanticResource.semanticProject.id,
      toaster,
    ]);

    const onClickRemoveFavorite = useCallback(async () => {
      const dataConnectionId =
        semanticResource.semanticProject.dataConnection?.id;
      if (!dataConnectionId) {
        return;
      }
      if (semanticResource.__typename === "SemanticDataset") {
        await pinData({
          kind: "semanticDataset",
          newPinnedState: false,
          originalDataset: semanticResource,
        });
      } else {
        await pinData({
          kind: "semanticView",
          newPinnedState: false,
          semanticViewId: semanticResource.id,
        });
      }
    }, [pinData, semanticResource]);

    const { userActionColor } = useTheme();
    return (
      <HexMenu>
        <FeatureGateToolTip
          action="explore outside of a published app"
          disabled={exploreFromScratchFeatureGate}
          featureGate="exploreFromScratch"
        >
          <HexMenuItem
            icon={<ExploreIcon />}
            text="Explore"
            onClick={createExploreAndTrack}
          />
        </FeatureGateToolTip>
        <HexMenuItem
          icon={<ExploreIcon />}
          text="Create Explore cell in new project"
          onClick={createExploreCell}
        />
        <HexMenuItem
          icon={<StarActiveIcon color={userActionColor} />}
          text="Remove favorite"
          onClick={onClickRemoveFavorite}
        />
      </HexMenu>
    );
  },
);
